import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon, cn, useQueryClient } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { afterDashboardSignOut } from "@apps/dashboard/lib/after-dashboard-sign-out";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { markProductTourPending } from "@apps/dashboard/features/onboarding/product-tour-storage";
import { translateAuthError } from "@apps/dashboard/features/auth/map-auth-error";
import { AccountDeleteConfirmDialog } from "@apps/dashboard/features/account/account-delete-confirm-dialog";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { UserAvatarImage } from "@apps/dashboard/components/UserAvatarImage";
import { ThemeModeControl } from "@apps/dashboard/components/theme/ThemeModeControl";
import { uploadStorageFile } from "@apps/dashboard/lib/home-expense-api";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import { isPasskeyAuthEnabled } from "@apps/dashboard/lib/env";

const AVATAR_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export default function SettingsPage(): JSX.Element {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const { data, refetch } = authClient.useSession();
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  const user = data?.user;
  const name = typeof user?.name === "string" && user.name.trim() ? user.name.trim() : null;
  const email = typeof user?.email === "string" ? user.email : "";
  const image =
    user && typeof user === "object" && "image" in user && typeof user.image === "string"
      ? user.image
      : null;

  async function handleSignOut(): Promise<void> {
    setBusy(true);
    try {
      await authClient.signOut();
      afterDashboardSignOut(queryClient);
      navigate(dashboardPaths.login, { replace: true });
    } finally {
      setBusy(false);
    }
  }

  async function handleAvatarFileChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setAvatarError(t("dashboardUi.profile.avatarUploadError"));
      return;
    }
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      const { key } = await uploadStorageFile(locale, file);
      const { error: err } = await authClient.updateUser({ image: key });
      if (err) {
        setAvatarError(translateAuthError(err, t));
        return;
      }
      await refetch();
    } catch (unknownErr) {
      setAvatarError(
        unknownErr instanceof ApiRequestError ? unknownErr.message : t("dashboardUi.profile.avatarUploadError"),
      );
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleClearAvatar(): Promise<void> {
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      const { error: err } = await authClient.updateUser({ image: null });
      if (err) {
        setAvatarError(translateAuthError(err, t));
        return;
      }
      await refetch();
    } catch (unknownErr) {
      setAvatarError(translateAuthError(unknownErr, t));
    } finally {
      setAvatarBusy(false);
    }
  }

  const rows: {
    to: string;
    title: string;
    hint: string;
    icon: "User" | "Shield" | "Fingerprint" | "Bell" | "HelpCircle" | "Smartphone";
  }[] = [
    {
      to: dashboardPaths.settingsPersonal,
      title: t("dashboardUi.settingsHub.cardPersonalDataTitle"),
      hint: t("dashboardUi.settingsHub.cardPersonalDataHint"),
      icon: "User",
    },
    {
      to: dashboardPaths.settingsPassword,
      title: t("dashboardUi.settingsHub.cardPasswordTitle"),
      hint: t("dashboardUi.settingsHub.cardPasswordHint"),
      icon: "Shield",
    },
    ...(isPasskeyAuthEnabled()
      ? [
          {
            to: dashboardPaths.settingsPasskeys,
            title: t("dashboardUi.settingsHub.cardPasskeysTitle"),
            hint: t("dashboardUi.settingsHub.cardPasskeysHint"),
            icon: "Fingerprint" as const,
          },
        ]
      : []),
    {
      to: dashboardPaths.settingsDevices,
      title: t("dashboardUi.settingsHub.cardDevicesTitle"),
      hint: t("dashboardUi.settingsHub.cardDevicesHint"),
      icon: "Smartphone",
    },
    {
      to: dashboardPaths.settingsNotifications,
      title: t("dashboardUi.settingsHub.cardNotificationsTitle"),
      hint: t("dashboardUi.settingsHub.cardNotificationsHint"),
      icon: "Bell",
    },
    {
      to: dashboardPaths.support,
      title: t("dashboardUi.settingsHub.cardSupportTitle"),
      hint: t("dashboardUi.settingsHub.cardSupportHint"),
      icon: "HelpCircle",
    },
  ];

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <input
        ref={avatarInputRef}
        type="file"
        accept={AVATAR_ACCEPT}
        className="sr-only"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => void handleAvatarFileChange(e)}
      />

      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
        {t("dashboardUi.settingsHub.unifiedPageTitle")}
      </h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">{t("dashboardUi.settingsHub.unifiedSubtitle")}</p>

      <section className="mb-10 mt-10 flex flex-col items-center text-center">
        <div className="relative mb-6">
          <div
            className={cn(
              "size-32 overflow-hidden rounded-full border-4 border-surface-container-lowest shadow-sm",
              avatarBusy && "opacity-60",
            )}
          >
            <UserAvatarImage
              rawImage={image}
              className="size-full object-cover"
              fallback={
                <div className="flex size-full items-center justify-center bg-primary/10 text-primary">
                  <Icon name="User" className="size-14" strokeWidth={1.25} />
                </div>
              }
            />
          </div>
          <button
            type="button"
            disabled={avatarBusy}
            onClick={() => avatarInputRef.current?.click()}
            className="absolute bottom-1 right-1 flex size-8 items-center justify-center rounded-full border-2 border-surface bg-primary text-on-primary shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t("dashboardUi.profile.avatarChangeAriaLabel")}
          >
            <Icon name="Pencil" className="size-4" strokeWidth={1.5} aria-hidden />
          </button>
        </div>
        {avatarBusy ? (
          <p className="mb-2 font-body text-xs text-on-surface-variant">{t("dashboardUi.profile.avatarUploading")}</p>
        ) : null}
        {avatarError ? (
          <p className="mb-2 max-w-sm font-body text-xs text-error" role="alert">
            {avatarError}
          </p>
        ) : null}
        {image ? (
          <button
            type="button"
            disabled={avatarBusy}
            onClick={() => void handleClearAvatar()}
            className="mb-4 font-body text-sm font-semibold text-on-surface-variant underline decoration-outline-variant hover:text-primary disabled:opacity-50"
          >
            {t("dashboardUi.profile.clearAvatarButton")}
          </button>
        ) : (
          <div className="mb-4 h-5" aria-hidden />
        )}
        <h2 className="font-headline text-xl font-bold tracking-tight text-on-surface">
          {name ?? (email || t("dashboardUi.shell.userStubName"))}
        </h2>
        {email ? <p className="mt-1 font-body text-sm text-on-surface-variant">{email}</p> : null}
      </section>

      <section className="space-y-4">
        <ThemeModeControl />
        <div className="overflow-hidden rounded-2xl border border-outline-variant/15 bg-surface-container-lowest shadow-sm">
          {rows.map(({ to, title, hint, icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex w-full items-center justify-between border-b border-outline-variant/10 px-6 py-5 transition-colors hover:bg-surface-container-low"
            >
              <div className="flex items-center gap-5">
                <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon name={icon} className="size-6" strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <p className="font-body font-semibold text-on-surface">{title}</p>
                  <p className="font-body text-xs text-on-surface-variant">{hint}</p>
                </div>
              </div>
              <Icon name="ChevronRight" className="size-5 text-outline transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
          <Link
            to={dashboardPaths.home}
            onClick={() => markProductTourPending()}
            className="group flex w-full items-center justify-between border-b border-outline-variant/10 px-6 py-5 transition-colors hover:bg-surface-container-low"
          >
            <div className="flex items-center gap-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                <Icon name="Sparkles" className="size-6" strokeWidth={1.5} />
              </div>
              <div className="text-left">
                <p className="font-body font-semibold text-on-surface">{t("dashboardUi.productTour.settingsReplay")}</p>
                <p className="font-body text-xs text-on-surface-variant">{t("dashboardUi.productTour.settingsReplayHint")}</p>
              </div>
            </div>
            <Icon name="ChevronRight" className="size-5 text-outline transition-transform group-hover:translate-x-1" />
          </Link>
          <button
            type="button"
            onClick={() => setDeleteAccountOpen(true)}
            className="group flex w-full items-center justify-between border-b border-outline-variant/10 px-6 py-5 text-left transition-colors hover:bg-error-container/15"
          >
            <div className="flex items-center gap-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-error/10 text-error">
                <Icon name="Trash2" className="size-6" strokeWidth={1.5} />
              </div>
              <div className="text-left">
                <p className="font-body font-semibold text-error">{t("dashboardUi.settingsHub.cardDeleteAccountTitle")}</p>
                <p className="font-body text-xs text-on-surface-variant">{t("dashboardUi.settingsHub.cardDeleteAccountHint")}</p>
              </div>
            </div>
            <Icon name="ChevronRight" className="size-5 text-outline transition-transform group-hover:translate-x-1" />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSignOut()}
            className={cn(
              "group flex w-full items-center justify-between px-6 py-5 transition-colors hover:bg-error-container/20 disabled:opacity-50",
            )}
          >
            <div className="flex items-center gap-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-error/10 text-error">
                <Icon name="LogOut" className="size-6" strokeWidth={1.5} />
              </div>
              <div className="text-left">
                <p className="font-body font-semibold text-error">{t("dashboardUi.profile.logout")}</p>
                <p className="font-body text-xs text-on-surface-variant">{t("dashboardUi.profile.logoutHint")}</p>
              </div>
            </div>
          </button>
        </div>
      </section>

      <footer className="mt-12 text-center">
        <p className="font-label text-[10px] uppercase tracking-widest text-outline">{t("dashboardUi.profile.footerNote")}</p>
      </footer>

      <AccountDeleteConfirmDialog open={deleteAccountOpen} onOpenChange={setDeleteAccountOpen} />
    </div>
  );
}
